import type { CustomRendererProps } from 'streamdown'
import * as React from 'react'

import { DiffErrorBoundary } from '@/components/DiffViewer/DiffErrorBoundary'
import { Skeleton } from '@/components/ui/skeleton'
import { CHAT_DIFF_MAX_LINE_CHARS } from '@/config/constants'

import { CodeBlock } from './code-block'

const DiffViewer = React.lazy(() =>
  import('@/components/DiffViewer').then((m) => ({ default: m.DiffViewer }))
)

export type DiffSection = {
  /** The `+++` name, minus any trailing `(proposed)`-style qualifier. */
  name?: string
  text: string
  /** False when the section carries no hunk and must stay plain code. */
  isDiff: boolean
}

const FILE_HEADER = /^(---|\+\+\+) /
// One hunk per section: `---` opens a new one only when `+++` follows or a
// blank line precedes it (how the tool joins fields), so a deleted SQL comment
// (`--- ` after its `-`) stays inside its diff.
const SECTION_START = /^(?=--- [^\n]*\n\+\+\+ )|(?<=\n\n)(?=--- )/m
const HUNK = /^@@ /
const DIFF_LINE = /^[ +-]?$/

/**
 * Normalizes a hunk body to what the library will accept, or null. The model's
 * own `@@` line is dropped and rebuilt: the library trusts its start line and
 * allocates from it, so `@@ -30000000` would freeze the tab. Any line that
 * breaks the ` `/`+`/`-` grammar rejects the whole body, since the library
 * throws on it, and a body with no change would render an empty card.
 */
function toHunk(lines: string[]): string | null {
  const body = lines.filter((l) => !HUNK.test(l) && !l.startsWith('\\'))
  if (body.some((l) => l.length > CHAT_DIFF_MAX_LINE_CHARS)) return null
  let oldCount = 0
  let newCount = 0
  for (const line of body) {
    if (!DIFF_LINE.test(line[0] ?? '')) return null
    if (line[0] !== '+') oldCount++
    if (line[0] !== '-') newCount++
  }
  if (oldCount === body.length && newCount === body.length) return null
  return `@@ -1,${oldCount} +1,${newCount} @@\n${body.join('\n')}`
}

/**
 * Splits a ```diff fence body into per-field sections. `preview_duty_diff`
 * joins several fields with blank lines, and a non-text field comes out as
 * `Current:` / `Proposed:` lines rather than hunks — that section is kept as
 * plain code instead of an empty card. David often strips the `---`/`+++`
 * headers, the `@@` line, or both when it copies the diff into its reply, so
 * each is synthesized when missing.
 */
export function parseDiffFence(code: string): DiffSection[] {
  // Only outer newlines: a leading space is a context line, not whitespace.
  const body = code.replace(/\r\n?/g, '\n').replace(/^\n+/, '').trimEnd()
  if (!body) return []
  return body
    .split(SECTION_START)
    .map((c) => c.trimEnd())
    .filter(Boolean)
    .map((chunk) => {
      const lines = chunk.split('\n')
      const headers: string[] = []
      while (FILE_HEADER.test(lines[0] ?? '')) headers.push(lines.shift()!)
      const name = headers
        .find((h) => h.startsWith('+++ '))
        ?.slice(4)
        .trim()
        .replace(/\s*\([^)]*\)$/, '')
      if (headers.length === 0) headers.push('--- a', '+++ b')
      const hunk = headers.length === 2 ? toHunk(lines) : null
      return {
        name: name || undefined,
        text: hunk ? [...headers, hunk].join('\n') : chunk,
        isDiff: hunk !== null,
      }
    })
}

const fallback = (
  <div className="py-6 text-center text-xs text-neutral-low-contrast">
    Couldn’t load the diff viewer. Reload to try again.
  </div>
)

/**
 * Streamdown custom renderer for ```diff fences: the house DiffViewer over the
 * unified diff David already gets back from `preview_duty_diff`. Read-only —
 * no apply, no accept/reject. When no section holds a real hunk it is a plain
 * code block, so a malformed payload never looks worse than without this.
 */
export function DiffRenderer({ code, isIncomplete }: CustomRendererProps) {
  // Same as the chart and mermaid fences: a skeleton while streaming, not a
  // syntax-highlighted re-render of the whole body on every token.
  if (isIncomplete) return <Skeleton className="h-24 w-full" />
  const sections = parseDiffFence(code)
  if (!sections.some((s) => s.isDiff)) {
    return <CodeBlock code={code} language="diff" />
  }
  return (
    <>
      {sections.map((section, i) =>
        section.isDiff ? (
          <div
            key={i}
            className="my-4 flex flex-col gap-2 rounded-lg border border-border bg-sidebar p-2"
            data-streamdown="diff-block">
            {section.name && (
              <span className="ml-1 truncate font-mono text-muted-foreground text-xs">
                {section.name}
              </span>
            )}
            <div className="overflow-x-auto rounded-md border border-border bg-background">
              <DiffErrorBoundary fallback={fallback}>
                <React.Suspense fallback={<Skeleton className="h-24 w-full" />}>
                  <DiffViewer
                    diff={section.text}
                    fileName={section.name}
                    mode="split"
                    fallback={<CodeBlock code={section.text} language="diff" />}
                  />
                </React.Suspense>
              </DiffErrorBoundary>
            </div>
          </div>
        ) : (
          <CodeBlock key={i} code={section.text} language="diff" />
        )
      )}
    </>
  )
}
